import React, { memo, useContext, useEffect, useRef } from 'react';
import type { HeadlineProps } from '@oneaudi/unified-web-components';
import { Headline } from '@oneaudi/unified-web-components';
import { renderTextWithFootnotesReferencesV2 } from '@oneaudi/feature-app-utils';
import styled from 'styled-components';
import { SYS_BREAKPOINT_MD } from '@oneaudi/unified-web-common';
import { sanitizeHtml, sanitizeText } from '../../utils/textUtils';
import type { Tracking } from '../../hooks/useTracking';
import { ContentContext } from '../../ContentContext';
import { FascinationContext } from '../../context/FascinationContext';

export const ClickableElement = styled.span`
  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    width: fit-content;
  }
  width: 100%;
  pointer-events: auto; // Make the element clickable
`;

interface StyledHeadlineProps extends HeadlineProps {
  readonly fascinationMode: boolean;
  readonly animate: boolean;
}

const StyledHeadline = styled(Headline)<StyledHeadlineProps>`
  #anchors-allowed > a {
    all: unset;
    text-decoration: underline !important;
    cursor: pointer;
    outline: none;
  }

  ${({ fascinationMode, animate }) =>
    !fascinationMode
      ? ''
      : `@media (prefers-reduced-motion: no-preference) {
    @keyframes fadeInUp {
      to {
        transform: translateY(0px);
        opacity: 1;
      }
    }

    transform: translateY(18px);
    opacity: 0;
    ${animate ? 'animation: fadeInUp 600ms cubic-bezier(0.215, 0.61, 0.355, 1) 1.5s forwards;' : ''}
  }`}
`;
export interface TextProps {
  readonly text: string;
  tracking: Tracking;
}

const RichTextAnchor = memo(function RichTextAnchor({ text, tracking, ...inherited }: TextProps) {
  const anchorTag = text.includes('</a>') || text.includes('</u>');
  const textRef = useRef<HTMLSpanElement | null>(null);
  const {
    content: { fascinationMode },
  } = useContext(ContentContext);
  const { animate } = useContext(FascinationContext);

  const content = anchorTag ? (
    <span id="anchors-allowed" dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }} />
  ) : (
    renderTextWithFootnotesReferencesV2(sanitizeText(text))
  );

  useEffect(() => {
    const container = textRef.current;
    const anchor = container?.querySelector('a');
    if (!anchor) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      tracking.sendClickEvent({
        url: (target.getAttribute('href') as string) || anchor.href,
        label: anchor.innerHTML, // link text of the clicked element
        index: 'link', // use string to differenciate anchor links from CTA in tracking eventName, elementName
        openInTabOrLayer: 'tab',
      });
    };
    anchor.addEventListener('click', handleClick);
    return () => {
      anchor.removeEventListener('click', handleClick);
    };
  }, []);
  return (
    <ClickableElement ref={textRef}>
      {/* @ts-expect-error types not aligned */}
      <StyledHeadline fascinationMode={fascinationMode} animate={animate} {...inherited}>
        {content}
      </StyledHeadline>
    </ClickableElement>
  );
});

export { RichTextAnchor };
